import React from 'react'
import { Input } from '@progress/kendo-react-inputs'
import { accentUtils, t, newGuid } from '../../services/HelperService'
import { AccentTooltip } from '../AccentTooltip'
import AccentIcons from '../../icons/Icons'
import { AccentLabel } from '../AccentLabel'

// `autoSize` inputs start at their CSS min-width and grow with the text. Browsers with
// `field-sizing: content` do that natively; elsewhere the text is measured in a hidden
// span with the input's font and the width set from that.
const supportsFieldSizing = () => typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("field-sizing", "content");

export class AccentTextUnbound extends React.Component {

    constructor(props) {
        super(props);

        this.ctrl = React.createRef();
        this.wrapper = React.createRef();
        this.measure = React.createRef();


        this.id = this.props.id;

        if (accentUtils.isNull(this.id)) {
            this.id = 'id_' + newGuid();
        }

        var value = this.props.defaultValue;

        this.state = { value: value, hideText: this.props.password, autoWidth: null };

        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onChange = this.onChange.bind(this);
        this.toggleHide = this.toggleHide.bind(this);
        
    }

    componentDidMount() {
        this.measureAutoWidth();
    }

    componentDidUpdate(prevProps) {
        this.measureAutoWidth();

        if (!this.props.resetWhenDefaultValueChanges) {
            return;
        }

        if (prevProps.defaultValue !== this.props.defaultValue && this.props.defaultValue !== this.state.value) {
            this.setState({ value: this.props.defaultValue });
        }
    }

    measureAutoWidth() {
        const span = this.measure.current;
        const input = this.wrapper.current?.querySelector("input");
        if (!span || !input) return;

        const style = window.getComputedStyle(input);
        span.style.font = style.font;
        span.style.letterSpacing = style.letterSpacing;
        span.textContent = `${this.state.value ?? ""}` || this.props.placeHolder || "";

        const chrome = ["paddingLeft", "paddingRight", "borderLeftWidth", "borderRightWidth"]
            .reduce((total, name) => total + (parseFloat(style[name]) || 0), 0);
        // A little slack so the caret after the last character is not clipped.
        const width = Math.ceil(span.offsetWidth + chrome + 4);

        if (width !== this.state.autoWidth) {
            this.setState({ autoWidth: width });
        }
    }

    onFocus(e) {

        if (this.props.onFocus) {
            this.props.onFocus(this.state.value);
        }
    }


    onBlur(e) {
        if (!accentUtils.isNull(this.props.onBlur)) {
            this.props.onBlur(this.state.value);
        }
    }

    onChange(e) {

        if (!accentUtils.isNull(this.props.onChange)) {
            this.props.onChange(accentUtils.isNull(e.target.value) ? null : e.target.value);
        }

        this.setState({ value: e.target.value })
    }

    toggleHide() {
        this.setState({hideText : !this.state.hideText});
    }

    render() {


        const measured = this.props.autoSize && !supportsFieldSizing();
        const style = {
            // Auto-sized: CSS `field-sizing: content` sets the width natively, or the
            // measured width is applied here; min/max come from the stylesheet.
            width: this.props.autoSize ? (measured && this.state.autoWidth !== null ? `${this.state.autoWidth}px` : undefined) : "100%",
            ...(this.props.style ?? {})
        };

        const formatValidationError = accentUtils.isNull(this.props.onValidate) ? null : this.props.onValidate(this.state.value);
        const isFormatValid = accentUtils.isEmpty(formatValidationError);


        var isValid = isFormatValid && ( !this.props.required || !accentUtils.isEmpty(this.state.value));

        var className = isValid ? "accentTextUnbound" : "accentTextUnbound invalid";


        let control = null;
        let input = null;

        if (this.props.password) {
            input = <Input
                className={className}
                value={this.state.value}
                type={this.state.hideText ? "password" : "text"}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onBlur={ this.onBlur}
                onKeyDown={this.props.onKeyDown}
                onContextMenu={this.props.onContextMenu}
                disabled={this.props.disabled}
                style={style}
                placeholder={this.props.placeHolder}
                maxLength={this.props.maxLength}
            />;
        } else {
            input = <Input
                id={this.id}
                className={className}
                value={this.state.value ?? ""}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                onKeyDown={this.props.onKeyDown}
                onContextMenu={this.props.onContextMenu}
                disabled={this.props.disabled}
                valid={isValid}
                style={style}
                placeholder={this.props.placeHolder}
                maxLength={this.props.maxLength}
                ariaLabel={accentUtils.isEmpty(this.props.ariaLabel) ? (accentUtils.isEmpty(this.props.label) ? undefined : t(this.props.label)) : this.props.ariaLabel}
            />;
        }

        const buttonedCtrl = accentUtils.isEmpty(this.props.buttons) ? input : <div className="d-flex"><div style={{ flex: 1 }}>{input}</div>{(this.props.buttons ?? []).map((b, i) => (<div key={ i}>{ b}</div>))}</div>;

        const labelCtrl = accentUtils.isEmpty(this.props.label) ? buttonedCtrl : <AccentLabel text={t(this.props.label)}>{buttonedCtrl}</AccentLabel>

        if (this.props.password) {
            control = <div className="acc-pass-ctrl" data-tagname={this.props.dataTagName}>
                {labelCtrl}
                <AccentIcons.Eye onClick={this.toggleHide} />
            </div>;
        }
        
        control = <div className={this.props.autoSize ? "acc-unbound-txt-ctrl accent-text-autosize" : "acc-unbound-txt-ctrl"} data-tagname={this.props.dataTagName} ref={this.wrapper}>
            {!isFormatValid && <AccentTooltip target={this.id} text={formatValidationError} />}
            {labelCtrl}
            {measured && <span className="accent-text-measure" aria-hidden="true" ref={this.measure} />}
            </div>;

        return control;
    }

}
